/**
 * useChatMessaging - Shared hook for chat messaging functionality
 *
 * Includes:
 * - Smart auto-scroll (only scrolls if user is at bottom)
 * - Send/edit/delete message handling
 * - Double-send protection
 * - Reply handling
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTeamChatStore, TeamChatMessage } from '../../../lib/stores/teamChatStore';
import { teamChatRealtimeService } from '../../../lib/services/teamChatRealtimeService';
import { useAuth } from '../../../lib/auth';

export function useChatMessaging() {
  const { userProfile } = useAuth();
  const {
    selectedChatId,
    selectedChatMessages,
    loadingMessages,
    loadingOlderMessages,
    hasMoreMessages,
    fetchOlderMessages,
    sendMessage,
    retryMessage,
    discardFailedMessage,
    editMessage,
    deleteMessage,
    replyingTo,
    setReplyingTo,
    clearTyping
  } = useTeamChatStore();

  const typingDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSendRef = useRef<number>(0);

  const messages = selectedChatMessages();

  // Local state
  const [inputValue, setInputValue] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isAtBottomRef = useRef(true);

  // Track if user is at bottom + infinite scroll verso l'alto
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 50;

    // Infinite scroll: carica messaggi piu vecchi quando si scrolla vicino al top
    if (scrollTop < 100 && selectedChatId && hasMoreMessages[selectedChatId] && !loadingOlderMessages) {
      const prevScrollHeight = container.scrollHeight;
      fetchOlderMessages(selectedChatId).then(() => {
        // Mantieni la posizione di scroll dopo il prepend
        requestAnimationFrame(() => {
          if (messagesContainerRef.current) {
            const newScrollHeight = messagesContainerRef.current.scrollHeight;
            messagesContainerRef.current.scrollTop = newScrollHeight - prevScrollHeight;
          }
        });
      });
    }
  }, [selectedChatId, hasMoreMessages, loadingOlderMessages, fetchOlderMessages]);

  // Smart auto-scroll: only scroll if user was already at bottom
  useEffect(() => {
    if (isAtBottomRef.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages]);

  // Focus input when replying
  useEffect(() => {
    if (replyingTo) {
      inputRef.current?.focus();
    }
  }, [replyingTo]);

  // Send message — fire-and-forget, non blocca l'input
  const handleSend = useCallback(() => {
    const now = Date.now();
    if (now - lastSendRef.current < 300 || !inputValue.trim() || !selectedChatId) return;
    lastSendRef.current = now;

    const messageToSend = inputValue.trim();
    const replyToId = replyingTo?.id;

    // Clear input immediately
    setInputValue('');

    // Ferma typing indicator
    if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
    if (selectedChatId && userProfile?.id) {
      clearTyping(selectedChatId, userProfile.id);
    }

    // Fire-and-forget: lo store gestisce ottimisticamente il messaggio
    // Se fallisce, il messaggio appare come "fallito" nella chat con retry
    sendMessage(selectedChatId, messageToSend, replyToId).catch(() => {
      // Il messaggio è già marcato come _status: 'failed' nello store
    });
  }, [inputValue, selectedChatId, replyingTo, sendMessage]);

  // Input change con typing indicator
  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);

    if (!selectedChatId || !userProfile?.id || !userProfile?.full_name) return;

    // Debounce: invia typing ogni 500ms max
    if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
    typingDebounceRef.current = setTimeout(() => {
      if (value.trim()) {
        teamChatRealtimeService.sendTyping(selectedChatId, userProfile.id, userProfile.full_name);
      }
    }, 300);
  }, [selectedChatId, userProfile]);

  // Start editing a message
  const startEditing = useCallback((message: TeamChatMessage) => {
    setEditingId(message.id);
    setEditValue(message.content);
  }, []);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setEditingId(null);
    setEditValue('');
  }, []);

  // Save edit
  const handleEdit = useCallback(async (messageId: string) => {
    if (!editValue.trim()) return;

    try {
      await editMessage(messageId, editValue.trim());
      setEditingId(null);
      setEditValue('');
    } catch (error) {
      console.error('[useChatMessaging] Error editing:', error);
      toast.error('Errore nella modifica del messaggio');
    }
  }, [editValue, editMessage]);

  // Delete message with confirmation
  const handleDelete = useCallback(async (messageId: string) => {
    if (!confirm('Eliminare questo messaggio?')) return;

    try {
      await deleteMessage(messageId);
    } catch (error) {
      console.error('[useChatMessaging] Error deleting:', error);
      toast.error('Errore nell\'eliminazione del messaggio');
    }
  }, [deleteMessage]);

  // Handle reply
  const handleReply = useCallback((message: TeamChatMessage) => {
    setReplyingTo(message);
    inputRef.current?.focus();
  }, [setReplyingTo]);

  // Cancel reply
  const cancelReply = useCallback(() => {
    setReplyingTo(null);
  }, [setReplyingTo]);

  // Handle key down in input (Enter to send, Escape to cancel reply)
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Don't send if composing (IME for CJK languages)
    if (e.nativeEvent.isComposing) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key === 'Escape' && replyingTo) {
      cancelReply();
    }
  }, [handleSend, replyingTo, cancelReply]);

  // Handle key down in edit input
  const handleEditKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, messageId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEdit(messageId);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  }, [handleEdit, cancelEditing]);

  return {
    // State
    messages,
    loadingMessages,
    loadingOlderMessages,
    inputValue,
    setInputValue,
    handleInputChange,
    editingId,
    editValue,
    setEditValue,
    replyingTo,

    // Refs
    messagesEndRef,
    messagesContainerRef,
    inputRef,

    // Handlers
    handleScroll,
    handleSend,
    handleKeyDown,
    startEditing,
    cancelEditing,
    handleEdit,
    handleEditKeyDown,
    handleDelete,
    handleReply,
    cancelReply,
    retryMessage,
    discardFailedMessage
  };
}
